import sanitizeHtml from "sanitize-html";

/**
 * Allow-list for full article bodies coming from the WordPress REST API.
 * WordPress "rendered" content is authored HTML we do not control end-to-end,
 * so it is sanitized before it reaches dangerouslySetInnerHTML.
 */
const ARTICLE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    ...sanitizeHtml.defaults.allowedTags,
    "img",
    "figure",
    "figcaption",
    "picture",
    "source",
    "h1",
    "h2",
    "span",
  ],
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    "*": ["id", "class", "style", "data-*", "lang", "dir"],
    a: ["href", "name", "target", "rel", "title"],
    img: ["src", "srcset", "sizes", "alt", "title", "width", "height", "loading", "decoding"],
    source: ["src", "srcset", "sizes", "type", "media"],
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: { img: ["http", "https", "data"] },
  // Inline styles stay limited to harmless typography/spacing. Without this an
  // unrestricted style attribute could still phone home via url(...) or overlay
  // the page (position/z-index) for clickjacking.
  allowedStyles: {
    "*": {
      color: [/^[^;{}()]+$/],
      "background-color": [/^[^;{}()]+$/],
      "text-align": [/^(left|right|center|justify)$/],
      "font-size": [/^[\d.]+(px|em|rem|%)$/],
      "font-weight": [/^(normal|bold|lighter|bolder|[1-9]00)$/],
      "font-style": [/^(normal|italic|oblique)$/],
      "text-decoration": [/^[a-z\s-]+$/],
      width: [/^[\d.]+(px|em|rem|%)$/],
      height: [/^[\d.]+(px|em|rem|%)$/],
      margin: [/^[\d.\sa-z%]+$/],
      padding: [/^[\d.\sa-z%]+$/],
    },
  },
  // Never let remote content smuggle in scripts or styles.
  disallowedTagsMode: "discard",
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { rel: "noopener noreferrer" }, true),
  },
};

/**
 * Narrow allow-list for short inline strings (headlines, teaser copy) that
 * carry light formatting from translation files or the CMS.
 */
const INLINE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ["b", "strong", "i", "em", "u", "s", "br", "span", "small", "sup", "sub", "a"],
  allowedAttributes: {
    span: ["class"],
    a: ["href", "target", "rel", "title"],
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { rel: "noopener noreferrer" }, true),
  },
};

/** Sanitize a full article body (WordPress post content). */
export function sanitizeArticleHtml(html: string): string {
  return sanitizeHtml(html ?? "", ARTICLE_OPTIONS);
}

/** Sanitize a short inline snippet (headline, description, ...). */
export function sanitizeInlineHtml(html: string): string {
  return sanitizeHtml(html ?? "", INLINE_OPTIONS);
}

/**
 * Escape a JSON-LD payload so it cannot break out of the surrounding
 * <script> element (e.g. via a literal "</script>" inside a string value).
 */
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}
